import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Crown, RefreshCw, Search, Check, EyeOff, Save, Pencil, ImagePlus, X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:   { label: 'Pending',   pill: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/50' },
  approved:  { label: 'Approved',  pill: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/50' },
  rejected:  { label: 'Rejected',  pill: 'bg-red-500/15 text-red-300 border-red-500/50' },
  anonymous: { label: 'Anonymous', pill: 'bg-zinc-700/40 text-zinc-300 border-zinc-600' },
};
const DEFAULT_STATUS = STATUS_META.pending;
const statusOf = (u) => STATUS_META[u.founder_name_status] || DEFAULT_STATUS;
const statusKeyOf = (u) => (STATUS_META[u.founder_name_status] ? u.founder_name_status : 'pending');

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Australia/Melbourne',
    });
  } catch { return iso; }
};

/**
 * FoundersTab - full admin control over the Founding Members wall.
 *
 * Each founder renders as a *photo-under-glass* card: a small brushed-gold
 * embossed frame with a portrait pane and a fully editable name field. If
 * no name is set yet, the field is prefilled with the generic Outlaw #N
 * label (paid order) so the admin can just tweak it. A tiny gold camera
 * icon opens the file picker to upload a portrait straight to R2 via the
 * existing presigned-URL flow.
 */
const FoundersTab = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [busy, setBusy] = useState(null);
  const [drafts, setDrafts] = useState({}); // { userId: { name?: string, photoBusy?: bool } }

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/founders/all`, { headers: auth });
      if (!res.ok) throw new Error('Failed to load founders');
      const data = await res.json();
      const list = Array.isArray(data?.items) ? data.items : [];
      setRows(list.map((r, i) => ({ id: r.id || r.email || `row-${i}`, ...r })));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const call = async (fn, userId) => {
    setBusy(userId);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const patch = (userId, body) => fetch(`${API}/api/admin/founders/${userId}`, {
    method: 'PATCH',
    headers: { ...auth, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const saveName = (u) => {
    const draft = (drafts[u.id]?.name ?? u.founder_display_name ?? u.default_name ?? '').trim();
    if (!draft) return;
    return call(async () => {
      const r = await patch(u.id, { founder_display_name: draft, auto_approve: true });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Save failed');
      }
      setDrafts((d) => { const n = { ...d }; delete n[u.id]; return n; });
    }, u.id);
  };

  const makeAnonymous = (u) => {
    if (!window.confirm(`Hide "${u.founder_display_name || u.default_name || u.email}" from the public Founders page?\n\nThe founding place stays counted, only their real name is removed - the plate will show ${u.default_name}.`)) return;
    return call(async () => {
      const r = await patch(u.id, { anonymous: true });
      if (!r.ok) throw new Error('Update failed');
    }, u.id);
  };

  const publish = (u) => call(async () => {
    const r = await fetch(`${API}/api/admin/founders/${u.id}/approve`, { method: 'POST', headers: auth });
    if (!r.ok) throw new Error('Approve failed');
  }, u.id);

  const clearPhoto = (u) => {
    if (!window.confirm('Remove this founder\'s photo?')) return;
    return call(async () => {
      const r = await patch(u.id, { clear_photo: true });
      if (!r.ok) throw new Error('Clear failed');
    }, u.id);
  };

  const uploadPhoto = async (u, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Only images allowed.'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Photo must be under 5 MB.'); return; }
    setDrafts((d) => ({ ...d, [u.id]: { ...(d[u.id] || {}), photoBusy: true } }));
    setError('');
    try {
      const pres = await fetch(`${API}/api/admin/founders/presign-photo`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, content_type: file.type }),
      });
      if (!pres.ok) {
        const j = await pres.json().catch(() => ({}));
        throw new Error(j.detail || 'Presign failed');
      }
      const { upload_url, public_url } = await pres.json();
      const put = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`R2 upload failed (${put.status})`);
      const save = await patch(u.id, { photo_url: public_url });
      if (!save.ok) throw new Error('Save failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setDrafts((d) => ({ ...d, [u.id]: { ...(d[u.id] || {}), photoBusy: false } }));
    }
  };

  const filtered = useMemo(() => {
    let list = rows;
    if (status !== 'all') list = list.filter((r) => statusKeyOf(r) === status);
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter((r) =>
        (r.email || '').toLowerCase().includes(needle) ||
        (r.founder_display_name || '').toLowerCase().includes(needle) ||
        (r.default_name || '').toLowerCase().includes(needle),
      );
    }
    return list;
  }, [rows, status, q]);

  const counts = useMemo(() => {
    const c = { all: rows.length, pending: 0, approved: 0, rejected: 0, anonymous: 0 };
    rows.forEach((r) => { const k = statusKeyOf(r); c[k] = (c[k] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div className="space-y-5" data-testid="founders-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Crown className="h-4 w-4 text-yellow-400" />
            <h3 className="text-sm font-black tracking-widest text-yellow-400 uppercase">Founding Members</h3>
          </div>
          <p className="text-sm text-gray-400">
            Manage the plates on the public{' '}
            <a href="/founders" target="_blank" rel="noreferrer" className="text-yellow-400 underline">Founders page</a>.
            Every founder starts with a generic <em className="text-yellow-300 not-italic">Outlaw #N</em> label
            (paid order). Edit the name and optionally upload a small photo - the plate becomes a portrait under
            glass in a gold embossed frame.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="founders-refresh"
          className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-yellow-900/40 text-gray-300 hover:text-white font-bold px-3 py-1.5 rounded text-xs"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="founders-filters">
        {['all', 'pending', 'approved', 'anonymous', 'rejected'].map((s) => {
          const isActive = status === s;
          const count = counts[s] ?? 0;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              disabled={s !== 'all' && count === 0}
              data-testid={`founders-filter-${s}`}
              className={`px-3 py-1.5 rounded text-[11px] font-black uppercase tracking-widest border transition-colors ${
                isActive
                  ? 'bg-yellow-500/15 text-yellow-300 border-yellow-500/60'
                  : 'bg-zinc-900 text-zinc-400 border-zinc-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed'
              }`}
            >
              {s} <span className="text-zinc-500">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email, name or Outlaw number…"
          data-testid="founders-search"
          className="w-full sm:max-w-sm bg-black/50 border border-yellow-500/25 text-white pl-9 pr-3 py-2 rounded text-sm focus:outline-none focus:border-yellow-500"
        />
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded text-sm" data-testid="founders-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400" data-testid="founders-loading">Loading founders…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-zinc-900/60 border border-yellow-900/30 rounded-lg p-8 text-center text-gray-400" data-testid="founders-empty">
          {rows.length === 0
            ? 'No Founding members yet. When someone pays for the Founding tier they show up here with a generic Outlaw #N label ready to edit.'
            : 'No founders match that search.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="founders-list">
          {filtered.map((u) => (
            <FounderPortraitCard
              key={u.id}
              u={u}
              draft={drafts[u.id]}
              busy={busy === u.id}
              onDraftChange={(name) => setDrafts((d) => ({ ...d, [u.id]: { ...(d[u.id] || {}), name } }))}
              onSaveName={() => saveName(u)}
              onCancelEdit={() => setDrafts((d) => { const n = { ...d }; delete n[u.id]; return n; })}
              onPublish={() => publish(u)}
              onAnonymous={() => makeAnonymous(u)}
              onUploadPhoto={(file) => uploadPhoto(u, file)}
              onClearPhoto={() => clearPhoto(u)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Photo-under-glass founder card.
 *
 * Anatomy: brushed-gold embossed frame → dark velvet mat → glass sheet (with
 * gloss highlight) → portrait or engraved Outlaw #N caption. Below the frame:
 * status pill, editable name field, and action buttons. Fully editable.
 */
const FounderPortraitCard = ({
  u, draft, busy,
  onDraftChange, onSaveName, onCancelEdit,
  onPublish, onAnonymous, onUploadPhoto, onClearPhoto,
}) => {
  const fileRef = useRef(null);
  const meta = statusOf(u);
  const statusKey = statusKeyOf(u);
  const isEditing = typeof draft?.name === 'string';
  const nameValue = isEditing ? draft.name : (u.founder_display_name || u.default_name || '');
  const photo = u.founder_photo_url;
  const photoBusy = draft?.photoBusy;

  return (
    <div
      data-testid={`founder-card-${u.id}`}
      className="relative rounded-lg p-4 flex gap-3 items-start"
      style={{
        background: 'linear-gradient(180deg, #12100a 0%, #0a0906 100%)',
        boxShadow: 'inset 0 0 0 1px rgba(212,170,80,0.25), 0 4px 14px rgba(0,0,0,0.7)',
      }}
    >
      {/* THE FRAME - gold embossed edge around the photo pane */}
      <div className="shrink-0" data-testid={`founder-frame-${u.id}`}>
        <div
          className="relative w-[92px] h-[92px] rounded-[6px] p-[3px]"
          style={{
            background: 'linear-gradient(135deg, #f6dc8a 0%, #c8942a 35%, #8a6015 65%, #f5dc95 100%)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.6), inset 0 1px 1px rgba(255,240,190,0.7), inset 0 -2px 3px rgba(0,0,0,0.35)',
          }}
        >
          {/* Inner mat + glass */}
          <div
            className="relative w-full h-full rounded-[3px] overflow-hidden"
            style={{
              background: photo
                ? 'linear-gradient(180deg, #1c1006 0%, #2a1a0a 100%)'
                : 'linear-gradient(180deg, #2a1808 0%, #140902 100%)',
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.9), inset 0 2px 3px rgba(0,0,0,0.55)',
            }}
          >
            {photo ? (
              <img
                src={photo}
                alt={u.founder_display_name || u.default_name || 'Founder portrait'}
                className="w-full h-full object-cover"
                data-testid={`founder-photo-${u.id}`}
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center text-center px-1"
                style={{
                  fontFamily: 'Georgia, serif',
                  color: '#f6dc8a',
                  textShadow: '0 1px 0 rgba(0,0,0,0.9)',
                }}
              >
                <span className="text-[10px] font-black uppercase tracking-[0.3em] leading-tight">
                  {u.default_name || `Outlaw #${u.outlaw_number || '?'}`}
                </span>
              </div>
            )}

            {/* Glass gloss highlight - diagonal sheen so it reads as glass */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.02) 32%, transparent 55%)',
              }}
            />
          </div>

          {/* Corner screw dots on the frame */}
          {['top-0.5 left-0.5', 'top-0.5 right-0.5', 'bottom-0.5 left-0.5', 'bottom-0.5 right-0.5'].map((pos, i) => (
            <span
              key={i}
              aria-hidden
              className={`absolute ${pos} h-1.5 w-1.5 rounded-full`}
              style={{
                background: 'radial-gradient(circle at 35% 30%, #f3d078, #7a5410 60%, #2a1a05 100%)',
                boxShadow: '0 1px 1px rgba(0,0,0,0.9)',
              }}
            />
          ))}
        </div>

        {/* Photo action row under the frame */}
        <div className="flex items-center gap-1 mt-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`founder-photo-input-${u.id}`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUploadPhoto(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={photoBusy || busy}
            data-testid={`founder-upload-${u.id}`}
            className="inline-flex items-center gap-1 bg-yellow-500/15 hover:bg-yellow-500/25 border border-yellow-500/40 text-yellow-200 disabled:opacity-40 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded"
          >
            <ImagePlus className="h-3 w-3" />
            {photoBusy ? '…' : photo ? 'Swap' : 'Photo'}
          </button>
          {photo && (
            <button
              type="button"
              onClick={onClearPhoto}
              disabled={busy}
              data-testid={`founder-clear-photo-${u.id}`}
              className="inline-flex items-center bg-zinc-800 hover:bg-zinc-700 text-zinc-300 disabled:opacity-40 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded"
              title="Remove photo"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* NAME + STATUS + ACTIONS */}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <p className="text-[10px] uppercase tracking-widest text-yellow-500/80 font-black">
            Seat #{u.outlaw_number}
          </p>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${meta.pill}`}
            data-testid={`founder-status-${u.id}`}
          >
            {statusKey === 'anonymous' ? <EyeOff className="h-2.5 w-2.5" /> : null}
            {meta.label}
          </span>
        </div>

        <input
          type="text"
          maxLength={40}
          value={nameValue}
          placeholder={u.default_name}
          onFocus={() => { if (!isEditing) onDraftChange(u.founder_display_name || u.default_name || ''); }}
          onChange={(e) => onDraftChange(e.target.value.slice(0, 40))}
          data-testid={`founder-name-input-${u.id}`}
          className="w-full bg-black/70 border border-yellow-500/30 focus:border-yellow-500 rounded px-2.5 py-1.5 text-white text-sm outline-none placeholder:text-zinc-500"
        />

        <p className="text-[10px] text-zinc-500 truncate font-mono" title={u.email}>{u.email || '—'}</p>
        <p className="text-[9px] text-zinc-600 uppercase tracking-widest">Paid {fmtDate(u.founding_purchased_at)}</p>

        <div className="flex items-center gap-1.5 flex-wrap pt-1">
          {isEditing ? (
            <>
              <button
                onClick={onSaveName}
                disabled={busy || !nameValue.trim()}
                data-testid={`founder-save-${u.id}`}
                className="inline-flex items-center gap-1 bg-emerald-700/50 hover:bg-emerald-600/70 text-emerald-100 disabled:opacity-40 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded"
              >
                <Save className="h-3 w-3" />
                Save + publish
              </button>
              <button
                onClick={onCancelEdit}
                data-testid={`founder-cancel-${u.id}`}
                className="text-zinc-400 hover:text-white text-[10px] font-black uppercase tracking-widest px-2 py-1"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onDraftChange(u.founder_display_name || u.default_name || '')}
                disabled={busy}
                data-testid={`founder-edit-${u.id}`}
                className="inline-flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded"
              >
                <Pencil className="h-3 w-3" /> Rename
              </button>
              {statusKey !== 'approved' && (u.founder_display_name || u.default_name) && (
                <button
                  onClick={onPublish}
                  disabled={busy}
                  data-testid={`founder-publish-${u.id}`}
                  className="inline-flex items-center gap-1 bg-emerald-700/40 hover:bg-emerald-600/60 text-emerald-100 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded"
                >
                  <Check className="h-3 w-3" /> Publish
                </button>
              )}
              {statusKey !== 'anonymous' && (
                <button
                  onClick={onAnonymous}
                  disabled={busy}
                  data-testid={`founder-anon-${u.id}`}
                  className="inline-flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded"
                >
                  <EyeOff className="h-3 w-3" /> Anonymous
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FoundersTab;
